import axios from 'axios'
import * as _ from 'lodash'

const studioUrl = process.env.REACT_APP_STUDIO_URL

let cachedAccessToken = null
let tokenExpiry = null

export const getSpeechLabAccessToken = async () => {
    const now = Date.now()
    if (cachedAccessToken && tokenExpiry && now < tokenExpiry) {
        return cachedAccessToken
    }
    try {
        const { data } = await axios.get(`${studioUrl}/api/ai/token`)
        if (data.accessToken) {
            cachedAccessToken = data.accessToken
            tokenExpiry = now + 2592000000 // 30 days
            return cachedAccessToken
        } else {
            throw new Error('Invalid response from studio service')
        }
    } catch (error) {
        console.error('Error getting SpeechLab access token:', error)
        throw error
    }
}

export const fetchMediaById = (id) => {
    return new Promise(async (resolve, reject) => {
        const { data } = await axios.get(`${studioUrl}/media`, {
            params: { id },
        })
        resolve(data)
    })
}

export const fetchPendingASRRequests = (id) => {
    return new Promise(async (resolve, reject) => {
        const { data } = await axios.get(`${studioUrl}/asr/current`, {
            params: { id },
        })
        resolve(data)
    })
}

export const fetchPendingICRRequests = (id) => {
    return new Promise(async (resolve, reject) => {
        const { data } = await axios.get(`${studioUrl}/icr/current`, {
            params: { id },
        })
        resolve(data)
    })
}

export const fetchPendingSERRequests = (id) => {
    return new Promise(async (resolve, reject) => {
        const { data } = await axios.get(`${studioUrl}/ser/current`, {
            params: { id },
        })
        resolve(data)
    })
}

export const deleteMediaById = async (id) => {
    try {
        const token = localStorage.getItem('token')
        await axios.delete(`${studioUrl}/media`, {
            params: { id },
            headers: { authorization: `Bearer ${token}` },
        })
        console.log(`Deleted ${id}!`)
        return true
    } catch (err) {
        throw err
    }
}

export const updateMediaById = (id, updates) => {
    const token = localStorage.getItem('token')
    return new Promise(async (resolve, reject) => {
        try {
            const { data } = await axios.put(
                `${studioUrl}/media`,
                { updates },
                {
                    params: { id },
                    headers: { authorization: `Bearer ${token}` },
                }
            )
            resolve(data)
        } catch ({ response }) {
            if (response) {
                reject(JSON.stringify(response.data))
            }
            reject(`Unknown or Network Connection error.`)
        }
    })
}

export const updateTranscriptById = async (id, name) => {
    const token = localStorage.getItem('token')
    console.log(id, name)
    return new Promise(async (resolve, reject) => {
        try {
            const { data } = await axios.put(
                `${studioUrl}/transcript`,
                { name },
                {
                    params: { id },
                    headers: { authorization: `Bearer ${token}` },
                }
            )
            resolve(data)
        } catch ({ response }) {
            if (response) {
                reject(JSON.stringify(response.data))
            }
            reject(`Unknown or Network Connection error.`)
        }
    })
}

export const deleteTranscriptById = async (id) => {
    try {
        const { data } = await axios.delete(`${studioUrl}/transcript`, {
            params: { id },
        })
        return data
    } catch ({ response }) {
        if (response) {
            throw Error(JSON.stringify(response.data))
        }
        throw Error(`Unknown or Network Connection error.`)
    }
}

export const processAutoTranscribe = (id, metadata) => {
    return new Promise(async (resolve, reject) => {
        try {
            const { data } = await axios.post(
                `${studioUrl}/asr/new`,
                { ...metadata },
                { params: { id } }
            )
            resolve(data)
        } catch (error) {
            const msg = _.get(error.response, 'data', 'Connection Error.')
            reject(msg)
        }
    })
}

export const processAutoImageCaption = (id) => {
    return new Promise(async (resolve, reject) => {
        try {
            const { data } = await axios.post(
                `${studioUrl}/icr/new`,
                {},
                {
                    params: { id },
                }
            )
            resolve(data)
            setTimeout(() => {
                resolve({ success: true })
            }, 1500)
        } catch (error) {
            const msg = _.get(error.response, 'data', 'Connection Error.')
            reject(msg)
        }
    })
}

export const ASR_IN_PROGRESS = 'Transcription in progress.'

export const fetchAutoTranscribeStatus = (id, asrId) => {
    return new Promise(async (resolve, reject) => {
        const { data } = await axios.get(`${studioUrl}/asr/status`, {
            params: { media: id, asr: asrId },
        })
        if (data.status === 'processing') {
            reject(ASR_IN_PROGRESS)
        }
        resolve('Transcription completed and attached to media.')
    })
}

export const ICR_IN_PROGRESS = 'Image caption in progress.'

export const fetchICRStatus = (id, icrId) => {
    return new Promise(async (resolve, reject) => {
        const { data } = await axios.get(`${studioUrl}/icr/status`, {
            params: { media: id, icr: icrId },
        })
        if (data.status === 'processing') {
            reject(ICR_IN_PROGRESS)
        }
        resolve('Image caption completed and attached to media.')
    })
}

export const fetchSEREngines = () => {
    return new Promise(async (resolve, reject) => {
        try {
            const { data } = await axios.get(`${studioUrl}/ser/engines`)
            resolve(data.engines || [])
        } catch (error) {
            const msg = _.get(error.response, 'data', 'Connection Error.')
            reject(msg)
        }
    })
}

export const processAutoSER = (id, transcriptId, engine) => {
    const body = {}
    if (transcriptId) body.transcript = transcriptId
    if (engine) body.engine = engine
    return new Promise(async (resolve, reject) => {
        try {
            const { data } = await axios.post(`${studioUrl}/ser/new`, body, {
                params: { id },
            })
            resolve(data)
        } catch (error) {
            const msg = _.get(error.response, 'data', 'Connection Error.')
            reject(msg)
        }
    })
}

export const SER_IN_PROGRESS = 'Emotion recognition in progress.'

export const fetchSERStatus = (id, serId) => {
    return new Promise(async (resolve, reject) => {
        const { data } = await axios.get(`${studioUrl}/ser/status`, {
            params: { media: id, ser: serId },
        })
        // /ser/status returns { success: true, status: 'completed' } only when
        // done; while running it returns { success: false, status: <engine> }.
        if (!data.success) {
            reject(SER_IN_PROGRESS)
        }
        resolve('Emotion recognition completed and attached to media as the default transcript.')
    })
}
